/**
 * ⛔⛔ THE RESOLVER HANDS OUT A GUESS WEARING A SUCCESS LABEL.
 * ==========================================================
 * GET /category-characteristics/resolve-sector ALWAYS returns `success: true` on a 200. When it
 * cannot place the category it still returns a `sectorId` — the FIRST of the candidate sectors —
 * and sets `resolved: false`:
 *
 *   backend-merged/services/SectorResolutionService.js:98  resolveWithFallback()
 *       findBestSectorForCategory() → null  ⇒  { sectorId: sectorIds[0], resolved: false }
 *   backend-merged/routes/category-characteristics.js:319  res.json({ success: true, sectorId, resolved, ... })
 *
 * ⇒ SO A CALLER THAT CHECKS `success` CANNOT TELL A RESOLUTION FROM A GUESS. Every fail-closed
 * guard built for this feature checked exactly that, which is why the same defect kept reappearing
 * on a new surface after each fix: we were treating symptoms of an API contract.
 *
 * 🔑 THIS FUNCTION EXISTS SO THE RULE IS TESTABLE AGAINST THE SHAPE THE BACKEND ACTUALLY SENDS,
 * NOT THE SHAPE THE FRONTEND EXPECTS. The previous tests asserted source shape and passed while
 * this hole was open — a test that encodes the caller's assumption cannot detect that the
 * assumption is wrong.
 *
 * ⚠️ MEASURED COST, so nobody discovers it in production. On the one live multi-sector site
 * (bikeinn, sectors {50,47}) an exact-leaf match exists for 67 of 132 researched categories, so
 * requiring `resolved === true` REFUSES ON ROUGHLY HALF of them. That is the intended direction on
 * a destructive path — a refusal is recoverable, a write to the wrong (sector, path) pair is not —
 * but it is a real operational cost and it is stated here rather than left to be found.
 * ⚠️ AND THE FIX FOR THAT IS NOT HERE: 125 of the same 132 match by FULL PATH, while the route
 * extracts the leaf before resolving. Filed, not changed — the route is a shared surface.
 */

export interface ResolveSectorResponse {
    success?: boolean;
    sectorId?: number | null;
    resolved?: boolean;
}

/**
 * The sector a DESTRUCTIVE write may use, or null when the answer is a guess or absent.
 *
 * ⛔ NULL MEANS "DO NOT WRITE". It does NOT mean "use the viewed sector" — that conflation is the
 * silent fallback this whole feature exists to remove.
 */
export function resolvedSectorForWrite(data: ResolveSectorResponse | null | undefined): number | null {
    if (!data) return null;
    if (data.success !== true) return null;
    // ⛔ THE LOAD-BEARING CLAUSE. `resolved === false` arrives alongside a perfectly valid-looking
    // sectorId, and that sectorId is simply the first candidate — not an answer about this category.
    if (data.resolved !== true) return null;
    return typeof data.sectorId === 'number' ? data.sectorId : null;
}
